import React, { useState, useEffect, useRef } from 'react';
import ReactDatePicker from 'react-datepicker';
import Select, { ValueType } from 'react-select';
import { Expenses } from '../../../models/unit-types.model';
import { Payout } from '../../../models/payout.model';
import _ from 'lodash';

export interface ExpensesListFilterContainerProps {
  data: Expenses[] | Payout[];
  entityLabel?: string;
  eventHandlers: {
    search: (data: {
      room: string;
      description: string;
      project: string;
      area: string;
      category: string;
      paymentDateFrom: Date;
      paymentDateTo: Date;
      amount: string;
    }) => void;
    getInitialList: () => void;
  };
}

export const ExpensesPayoutListFilterContainer: React.FC<ExpensesListFilterContainerProps> = ({
  data,
  entityLabel = 'expenses',
  eventHandlers
}) => {
  const [showMoreFilters, setShowMoreFilters] = useState<boolean>(false);
  const isFirstRender = useRef(true);
  const [description, setDescription] = useState<string>('');
  const [room, setRoom] = useState<{ value: string; label: string }>({
    value: '',
    label: ''
  });
  const [project, setProject] = useState<{ value: string; label: string }>({
    value: '',
    label: ''
  });
  const [area, setArea] = useState<{ value: string; label: string }>({ value: '', label: '' });
  const [category, setCategory] = useState<{ value: string; label: string }>({
    value: '',
    label: ''
  });
  const [paymentDateFrom, setPaymentDateFrom] = useState<Date>();
  const [paymentDateTo, setPaymentDateTo] = useState<Date>();
  const [amount, setAmount] = useState<{ value: string; label: string }>({
    value: '',
    label: ''
  });
  const [initialAmountOptions, setInitialAmountOptions] = useState<
    {
      value: string;
      label: string;
      roomId: string;
      areaId: string;
      projectId: string;
      category: string;
    }[]
  >([]);
  const [projectsList, setProjectsList] = useState<
    {
      value: string;
      label: string;
      roomId: string;
      areaId: string;
      category: string;
      amount: string;
    }[]
  >([]);
  const [initialProjectsList, setInitialProjectsList] = useState<
    {
      value: string;
      label: string;
      roomId: string;
      areaId: string;
      category: string;
      amount: string;
    }[]
  >([]);
  const [areaOptions, setAreaOptions] = useState<
    {
      value: string;
      label: string;
      projectId: string;
      roomId: string;
      category: string;
      amount: string;
    }[]
  >([]);

  const [roomsOptions, setRoomsOptions] = useState<
    {
      value: string;
      label: string;
      projectId: string;
      areaId: string;
      category: string;
      amount: string;
    }[]
  >([]);
  const [initialRoomsOptions, setInitialRoomsOptions] = useState<
    {
      value: string;
      label: string;
      projectId: string;
      areaId: string;
      category: string;
      amount: string;
    }[]
  >([]);
  const [categoryOptions, setCategoryOptions] = useState<
    {
      value: string;
      label: string;
      roomId: string;
      areaId: string;
      projectId: string;
      amount: string;
    }[]
  >([]);
  const [initialCategoryOptions, setInitialCategoryOptions] = useState<
    {
      value: string;
      label: string;
      roomId: string;
      areaId: string;
      projectId: string;
      amount: string;
    }[]
  >([]);
  const [amountOptions, setAmountOptions] = useState<
    {
      value: string;
      label: string;
      roomId: string;
      areaId: string;
      projectId: string;
      category: string;
    }[]
  >([]);

  useEffect(() => {
    const hasFilters =
      description ||
      area.label ||
      project.label ||
      room.label ||
      category.label ||
      amount.label ||
      paymentDateFrom ||
      paymentDateTo;
    if (data?.length > 0 && !hasFilters) {
      filterDataWithoutDuplicate(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const searchExpenses = () => {
    const query: any = {};
    if (room.label && room.label !== '') query.roomName = room?.label;
    if (description !== '') query.description = description;
    if (project.label !== '') query.projectName = project?.label;
    if (area.label !== '') query.areaName = area?.label;
    if (category.label && category.label !== '') query.category = category?.label;
    if (paymentDateFrom) query.from = paymentDateFrom?.getTime();
    if (paymentDateTo) query.to = paymentDateTo?.getTime();
    if (amount.label !== '') query.amount = amount?.label;
    eventHandlers.search(query);
  };

  const clearSearch = () => {
    setDescription('');
    setProject({ value: '', label: '' });
    setCategory({ value: '', label: '' });
    setAmount({ value: '', label: '' });
    setRoom({ value: '', label: '' });
    setArea({ value: '', label: '' });
    setPaymentDateFrom(undefined);
    setPaymentDateTo(undefined);
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const hasFilters =
        description ||
        area.label ||
        project.label ||
        room.label ||
        category.label ||
        amount.label ||
        paymentDateFrom ||
        paymentDateTo;
      if (hasFilters) {
        searchExpenses();
      } else {
        eventHandlers.getInitialList();
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [description, area, project, room, category, amount, paymentDateFrom, paymentDateTo]);
  // initiate filter items options
  const filterDataWithoutDuplicate = (data: Expenses[] | Payout[]) => {
    const area: {
      value: string;
      label: string;
      projectId: string;
      roomId: string;
      category: string;
      amount: string;
    }[] = [];

    const rooms: {
      value: string;
      label: string;
      projectId: string;
      areaId: string;
      category: string;
      amount: string;
    }[] = [];

    const projects: {
      value: string;
      label: string;
      roomId: string;
      areaId: string;
      category: string;
      amount: string;
    }[] = [];

    const category: {
      value: string;
      label: string;
      roomId: string;
      areaId: string;
      projectId: string;
      amount: string;
    }[] = [];

    const amount: {
      value: string;
      label: string;
      roomId: string;
      areaId: string;
      projectId: string;
      category: string;
    }[] = [];

    data?.map((unit) => {
      if (unit?.areaId !== undefined && unit?.areaName !== undefined) {
        area.push({
          value: unit?.areaId,
          label: unit?.areaName,
          projectId: unit?.projectId,
          roomId: unit?.roomId,
          category: unit?.category || '',
          amount: unit?.amount?.toString()
        });
      }
      if (
        unit?.roomId !== undefined &&
        unit?.roomName !== undefined &&
        unit?.projectId !== undefined
      ) {
        rooms.push({
          value: unit.roomId,
          label: unit.roomName,
          projectId: unit?.projectId,
          areaId: unit.areaId || '',
          category: unit?.category || '',
          amount: unit?.amount?.toString()
        });
      }
      if (
        unit?.projectId !== undefined &&
        unit?.projectName !== undefined &&
        unit?.areaId !== undefined
      ) {
        projects.push({
          value: unit?.projectId,
          label: unit?.projectName,
          roomId: unit.roomId,
          areaId: unit.areaId,
          category: unit?.category || '',
          amount: unit?.amount?.toString()
        });
      }
      if (unit?.category !== undefined && unit?.roomId !== undefined) {
        category.push({
          value: unit?.category,
          label: unit?.category,
          roomId: unit.roomId,
          areaId: unit.areaId || '',
          projectId: unit?.projectId,
          amount: unit?.amount?.toString()
        });
      }
      if (unit?.amount && unit?.category !== undefined) {
        amount.push({
          value: unit?.amount.toString(),
          label: unit?.amount.toString(),
          roomId: unit?.roomId,
          areaId: unit?.areaId || '',
          projectId: unit?.projectId,
          category: unit?.category
        });
      }
    });

    if (area.length > 0) {
      setAreaOptions(_.uniqBy(area, 'value'));
    }
    if (rooms.length > 0) {
      setRoomsOptions(_.uniqBy(rooms, 'value'));
      setInitialRoomsOptions(_.uniqBy(rooms, 'value'));
    }
    if (projects.length > 0) {
      setProjectsList(_.uniqBy(projects, 'value'));
      setInitialProjectsList(_.uniqBy(projects, 'value'));
    }
    if (category.length > 0) {
      setCategoryOptions(_.uniqBy(category, 'value'));
      setInitialCategoryOptions(_.uniqBy(category, 'value'));
    }
    if (amount.length > 0) {
      setAmountOptions(_.uniqBy(amount, 'value'));
      setInitialAmountOptions(_.uniqBy(amount, 'value'));
    }
  };

  const toOption = (option: { value: string; label: string }) =>
    option.label ? { value: option.value, label: option.label } : null;
  const pillClassName = (isActive: boolean) => `pill-select ${isActive ? 'is-active' : ''}`;
  const hasActiveFilters = !!(
    description ||
    area.label ||
    project.label ||
    room.label ||
    category.label ||
    amount.label ||
    paymentDateFrom ||
    paymentDateTo
  );
  const moreFiltersCount = [category.label, amount.label].filter(Boolean).length;
  const formatShortDate = (date?: Date) =>
    date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

  return (
    <section
      aria-label={`Filter ${entityLabel}`}
      className="rounded border border-slate-200/80 bg-white px-3.5 py-2 shadow-sm"
    >
      <div className="flex flex-col items-center justify-between gap-2.5 md:flex-row">
        <div className="flex w-full flex-1 flex-wrap items-center gap-2 md:w-auto">
          <div className="relative min-w-[240px] flex-1 sm:max-w-xs">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
            </div>
            <input
              type="text"
              aria-label="Search description"
              value={description}
              placeholder="Search by description..."
              onChange={(event) => setDescription(event.target.value)}
              className="block w-full rounded-full border border-slate-200/80 bg-slate-50/80 py-2 pl-9 pr-3 text-sm font-medium text-slate-800 placeholder-slate-400 transition-all hover:bg-slate-100/60 focus:border-primary-500 focus:bg-white focus:outline-none"
            />
          </div>
          <div className="mx-0.5 hidden h-4 w-px bg-slate-200/80 sm:block" />

          <div className="w-44">
            <Select
              aria-label="Area"
              className={pillClassName(!!area.label)}
              classNamePrefix="pill-select"
              isClearable
              value={toOption(area)}
              placeholder="Area: All"
              options={areaOptions?.map((user) => ({
                value: user?.value,
                label: user?.label
              }))}
              onChange={(value: ValueType<{ value: string; label: string }, false>) => {
                setArea(value ? value : { value: '', label: '' });
                setProjectsList(
                  initialProjectsList?.filter((project) =>
                    project?.areaId?.includes(value?.value || '')
                  )
                );
                setProject({ value: '', label: '' });
                setCategory({ value: '', label: '' });
                setAmount({ value: '', label: '' });
                setRoom({ value: '', label: '' });
              }}
            />
          </div>
          <div className="w-48">
            <Select
              aria-label="Project"
              className={pillClassName(!!project.label)}
              classNamePrefix="pill-select"
              isClearable
              value={toOption(project)}
              placeholder="Project: All"
              options={projectsList.map((unitType) => ({
                value: unitType.value,
                label: unitType.label
              }))}
              onChange={(value: ValueType<{ value: string | undefined; label: string }, false>) => {
                setProject(
                  value
                    ? { value: value.value || '', label: value.label }
                    : { value: '', label: '' }
                );
                setCategory({ value: '', label: '' });
                setAmount({ value: '', label: '' });
                setRoom({ value: '', label: '' });
                setRoomsOptions(
                  initialRoomsOptions?.filter((unit) =>
                    unit?.projectId?.includes(value?.value || '')
                  )
                );
              }}
            />
          </div>
          <div className="w-44">
            <Select
              aria-label="Room"
              className={pillClassName(!!room.label)}
              classNamePrefix="pill-select"
              isClearable
              value={toOption(room)}
              placeholder="Room: All"
              options={roomsOptions.map((room) => ({
                value: room.value,
                label: room.label
              }))}
              onChange={(value: ValueType<{ value: string | undefined; label: string }, false>) => {
                setRoom(
                  value
                    ? { value: value.value || '', label: value.label }
                    : { value: '', label: '' }
                );
                setCategory({ value: '', label: '' });
                setAmount({ value: '', label: '' });
                setCategoryOptions(
                  initialCategoryOptions?.filter((unit) =>
                    unit?.roomId?.includes(value?.value || '')
                  )
                );
              }}
            />
          </div>
          <ReactDatePicker
            selectsRange
            startDate={paymentDateFrom}
            endDate={paymentDateTo}
            onChange={(dates) => {
              const [from, to] = dates as unknown as [Date | null, Date | null];
              setPaymentDateFrom(from || undefined);
              setPaymentDateTo(to || undefined);
            }}
            isClearable={!!paymentDateFrom}
            customInput={
              <button
                type="button"
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium shadow-sm transition-all ${
                  paymentDateFrom
                    ? 'border-primary-500 bg-primary-50 text-primary-800'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                {paymentDateFrom
                  ? `${formatShortDate(paymentDateFrom)} – ${formatShortDate(paymentDateTo) || '…'}`
                  : 'Payment date'}
                <svg
                  className="h-3.5 w-3.5 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                  />
                </svg>
              </button>
            }
          />
        </div>

        <div className="flex shrink-0 items-center gap-2 self-end md:self-auto">
          <button
            type="button"
            aria-expanded={showMoreFilters}
            onClick={() => setShowMoreFilters((current) => !current)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-5 py-2.5 text-base font-medium shadow-sm transition-all ${
              showMoreFilters || moreFiltersCount
                ? 'border-primary-500 bg-primary-50 text-primary-800'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            <svg
              className="h-5 w-5 text-slate-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
            More Filters
            {moreFiltersCount > 0 && (
              <span className="rounded-full bg-primary-500 px-2 text-sm font-bold text-white">
                {moreFiltersCount}
              </span>
            )}
          </button>
          <button
            type="button"
            disabled={!hasActiveFilters}
            onClick={() => clearSearch()}
            className="rounded-lg px-3 py-2 text-base font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 disabled:cursor-default disabled:text-slate-300 disabled:hover:bg-transparent"
          >
            Reset
          </button>
        </div>
      </div>

      {showMoreFilters && (
        <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2">
          <div className="w-52">
            <Select
              aria-label="Category"
              className={pillClassName(!!category.label)}
              classNamePrefix="pill-select"
              isClearable
              value={toOption(category)}
              placeholder="Category: All"
              options={categoryOptions.map((category) => ({
                value: category.value,
                label: category.label
              }))}
              onChange={(value: ValueType<{ value: string | undefined; label: string }, false>) => {
                setCategory(
                  value
                    ? { value: value.value || '', label: value.label }
                    : { value: '', label: '' }
                );
                setAmount({ value: '', label: '' });
                setAmountOptions(
                  initialAmountOptions?.filter((unit) =>
                    unit?.category?.includes(value?.value || '')
                  )
                );
              }}
            />
          </div>
          <div className="w-48">
            <Select
              aria-label="Amount"
              className={pillClassName(!!amount.label)}
              classNamePrefix="pill-select"
              isClearable
              value={toOption(amount)}
              placeholder="Amount: All"
              options={amountOptions.map((amount) => ({
                value: amount.value,
                label: amount.label
              }))}
              onChange={(value: ValueType<{ value: string | undefined; label: string }, false>) => {
                setAmount(
                  value
                    ? { value: value.value || '', label: value.label }
                    : { value: '', label: '' }
                );
              }}
            />
          </div>
        </div>
      )}
    </section>
  );
};
